import { Component, ContentChild, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { provideParentForm } from '@app/shared/providers/provide-parent-form.provider';
import { BaseFormComponentDirective, getBaseFormComponentDirectiveProvider } from '../base-form-component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

interface IItemDto {
    id: number;
    [key: string]: any;
}

/**
 * Component that is used to build a collection of anything, using any form controls to build said collection.
 * Displays the collection in a cb-table.
 *
 * Sample usage:
 *
        <cb-collection-builder [displayedColumns]="displayedColumns"
                               [(newCollectionItem)]="newCollectionItem"
                               (collectionItemAdded)="collectionItemAdded($event)">
            <ng-container addCollectionItemRow>
                <td>
                    <cb-input type="text"
                              name="newItemExternalId"
                              [(ngModel)]="newCollectionItem.externalReferenceId">
                    </cb-input>
                </td>
                <td>
                    <cb-select name="newItemExternalSystem"
                               [(ngModel)]="newCollectionItem.externalSystem"
                               [options]="externalSystemList">
                    </cb-select>
                </td>
            </ng-container>
            <ng-template #collectionItemColumns
                         let-collectionItem>
                <cb-td-text [value]="collectionItem.externalReferenceId">
                </cb-td-text>
                <cb-td-enum [value]="collectionItem.externalSystem"
                            [enum]="EXTERNAL_SYSTEM_ENUM">
                </cb-td-enum>
            </ng-template>
        </cb-collection-builder>
 */
@Component({
    selector: 'cb-collection-builder',
    templateUrl: './collection-builder.component.html',
    styleUrls: ['./collection-builder.component.scss'],
    providers: [
        ...getBaseFormComponentDirectiveProvider(CollectionBuilderComponent),
    ],
    viewProviders: [
        provideParentForm(),
    ],
    standalone: false
})
export class CollectionBuilderComponent extends BaseFormComponentDirective {
    @Input() public displayedColumns: string[];
    @Input() public newCollectionItem: IItemDto;
    @Input() public addButtonDisabled: boolean;
    @Output() public collectionItemAdded = new EventEmitter();
    @Input() public collection: Array<IItemDto>;
    @Output() public collectionItemRemoved = new EventEmitter();

    @ContentChild('collectionItemColumns', { static: false }) public collectionItemColumns: TemplateRef<any>;

    constructor() {
        super();
    }

    public drop(event: CdkDragDrop<IItemDto[]>): void {
        if (!this.collection || event.previousIndex === event.currentIndex) return;

        moveItemInArray(this.collection, event.previousIndex, event.currentIndex);

        const sortOrders: { [collectionItemId: number]: number } = {};
        this.collection.forEach((x, i) => {
            x[this.sortOrderValueProp] = i + 1;
            sortOrders[x.id] = x[this.sortOrderValueProp];
        });

        this.sortOrderChanged.emit(sortOrders);
    }

    public addItem(): void {
        if (this.newCollectionItem) {
            this.collection = this.collection ? [...this.collection, this.newCollectionItem] : [this.newCollectionItem];
        }
        this.collectionItemAdded.emit(this.collection);
    }

    public removeItem(index: number): void {
        const removedItem = this.collection[index];
        this.collection.splice(index, 1);
        this.collectionItemRemoved.emit({ collection: this.collection, removedItem });
    }

    @Output() public readonly sortOrderChanged = new EventEmitter<{ [collectionItemId: number]: number }>();
    @Input() public sortOrderValueProp: string;
}
